import { INSTALLATION_HEIGHT_KEYS } from "../../installationHeights";
import type { GapTranslate } from "../../findCableGaps";
import type { HeightTarget } from "../../installationHeights";
import type { KarlAnswer, KarlAsk, KarlAskFocus } from "../types";
import type { BuildHeightAsks } from "./types";


const line = (t: GapTranslate, key: string, params?: Record<string, string | number>): string =>
  t(`smartSurveyor.cablesBeta.${key}`, params);

const later = (t: GapTranslate): KarlAnswer => ({
  id: "later",
  label: line(t, "askLater"),
  tone: "quiet",
  action: { kind: "skip" },
});

// Typing a height IS the answer, so changing it is a rung of the ladder, not a tool beside it.
export const heightAnswers = (t: GapTranslate, target: HeightTarget, heightM: number): KarlAnswer[] => [
  {
    id: "right",
    label: line(t, "askHeightRight"),
    tone: "primary",
    action: { kind: "setHeight", target, heightM },
  },
  {
    id: "change",
    label: line(t, "askHeightChange"),
    action: { kind: "editHeight", target, heightM },
  },
  later(t),
];

interface HeightAskArgs {
  id: string;
  group: KarlAsk["group"];
  focus?: KarlAskFocus;
  target: HeightTarget;
  heightM: number;
  question: string;
  detail?: string;
  subject?: string;
  t: GapTranslate;
}

const heightAsk = ({ id, group, focus, target, heightM, question, detail, subject, t }: HeightAskArgs): KarlAsk => ({
  id,
  group,
  question,
  detail,
  subject,
  subjectKey: id,
  ...(focus ? { focus } : {}),
  answers: heightAnswers(t, target, heightM),
});

export const buildHeightAsks: BuildHeightAsks = ({ heights, t }) => {
  const levels = INSTALLATION_HEIGHT_KEYS.map((key) => ({
    ...heightAsk({
      id: `height:level:${key}`,
      group: "heights",
      target: { kind: "level", key },
      heightM: heights.resolved.levels[key],
      question: line(t, `askHeight_${key}`, { height: heights.format(heights.resolved.levels[key]) }),
      detail: line(t, `askHeightDetail_${key}`),
      t,
    }),
    isStored: heights.stored.installationHeights?.[key] !== undefined,
  }));
  const trays = heights.trayTypes.map((trayType) => {
    const tray = trayType || line(t, "heightTrayUntyped");
    return {
      ...heightAsk({
        id: `tray:height:${trayType}`,
        group: "trays",
        focus: { positions: heights.trayPositions[trayType] ?? [], trayType },
        target: { kind: "tray", trayType },
        heightM: heights.resolved.trayM(trayType),
        question: line(t, "askHeightTray", { tray, height: heights.format(heights.resolved.trayM(trayType)) }),
        detail: line(t, "askHeightTrayDetail"),
        subject: tray,
        t,
      }),
      isStored: heights.stored.trayHeights?.[trayType] !== undefined,
    };
  });
  // No closing "all set — carry on?": it asked nothing he had not just answered. The step closes on its last height.
  return [...levels, ...trays];
};
